import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HbrTab, HbrTabGroup } from '@harbor/elements-react-standalone';
import NavHistoryTracker from '../../../navigation/NavHistoryTracker';
import { useAdminPermissions } from '../Hooks/useAdminPermissions';
import { ADMIN_URL_LABS_EVENTS } from './admin-values';
import LabAgentsPanel from '../../Lab/LabAgentsPanel';
import LabAssignmentsPanel from '../../Lab/LabAssignmentsPanel';
import LabAssistPanel from '../../Lab/LabAssistPanel';
import LabEventsPanel from '../../Lab/LabEventsPanel';
import LabInjectionsPanel from '../../Lab/LabInjectionsPanel';
import LabPoolsPanel from '../../Lab/LabPoolsPanel';
import LabSweepsPanel from '../../Lab/LabSweepsPanel';

/**
 * Delivering the labs: which event, on which pods, to whom.
 *
 * An event names a published lab and a time. What comes up is a dCloud pod from that
 * configuration's template - the scenario is layered on afterwards, once the session goes active -
 * so nothing on this page edits a lab. That split is the backend's as well: a half-written step
 * cannot reach a running pod because somebody saved it.
 *
 * The operational tabs are the same panels the lab area uses. They are not duplicated here; a
 * proctor mid-event and an admin looking at the same pool should not be reading two different
 * tables.
 */
const labEventModels = {
  events: {
    key: 'events',
    label: 'Events',
    pageView: 'page.labs.view',
  },
  pools: {
    key: 'pools',
    label: 'Pools',
    pageView: 'page.labs.view',
  },
  assignments: {
    key: 'assignments',
    label: 'Assignments',
    pageView: 'page.labs.view',
  },
  assist: {
    key: 'assist',
    label: 'Assist',
    pageView: 'page.labs.view',
  },
  agents: {
    key: 'agents',
    label: 'Agents',
    pageView: 'page.labs.view',
  },
  sweeps: {
    key: 'sweeps',
    label: 'Sweeps',
    pageView: 'page.labs.view',
  },
  injections: {
    key: 'injections',
    label: 'Mid-session pushes',
    pageView: 'page.labs.view',
  },
};

const AdminLabEvents = () => {
  const { section } = useParams();
  const navigate = useNavigate();
  const { isVisibleTab, isAdminFull } = useAdminPermissions();

  const modelKeys = useMemo(
    () => Object.keys(labEventModels).filter((key) => isVisibleTab(labEventModels[key])),
    // See AdminLabGeneration: `page.labs.view` is not seeded on most tenants yet, so an empty
    // result falls back to the full list rather than an admin page with no tabs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isVisibleTab],
  );

  const visibleKeys = modelKeys.length ? modelKeys : Object.keys(labEventModels);

  const validTab = useMemo(
    () => (section && visibleKeys.includes(section) ? section : visibleKeys[0]),
    [section, visibleKeys],
  );

  const [currentTabKey, setCurrentTabKey] = useState<string | undefined>(validTab);

  useEffect(() => {
    setCurrentTabKey(validTab);
  }, [validTab]);

  const handleTabClick = (key: string) => {
    setCurrentTabKey(key);
    navigate(`${ADMIN_URL_LABS_EVENTS}/${key}`);
  };

  // Everything on this page is a steward action - stocking, assigning, enrolling, pushing - and
  // the backend authorizes each one again against the caller's token.
  const canSteward = isAdminFull || isVisibleTab(labEventModels.events);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <NavHistoryTracker title={currentTabKey} />

      <h1 className="mt-0 pb-6 border-b border-gray-15 text-gray-60 font-semibold leading-5">
        Event Management
      </h1>

      <HbrTabGroup primary className="my-4" style={{ display: 'block', flexShrink: 0 }}>
        {visibleKeys.map((key) => (
          <HbrTab
            slot="nav"
            panel={key}
            key={key}
            onClick={() => handleTabClick(key)}
            active={key === currentTabKey}
          >
            {labEventModels[key].label}
          </HbrTab>
        ))}
      </HbrTabGroup>

      <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {currentTabKey === 'events' && <LabEventsPanel canEdit={canSteward} />}
        {currentTabKey === 'pools' && <LabPoolsPanel canSteward={canSteward} />}
        {currentTabKey === 'assignments' && <LabAssignmentsPanel canSteward={canSteward} />}
        {currentTabKey === 'assist' && <LabAssistPanel />}
        {currentTabKey === 'agents' && <LabAgentsPanel canManage={canSteward} />}
        {currentTabKey === 'sweeps' && <LabSweepsPanel />}
        {currentTabKey === 'injections' && <LabInjectionsPanel canManage={canSteward} />}
      </div>
    </div>
  );
};

export default AdminLabEvents;
